import { forwardRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getIcon } from '@/utils/icon-utils';
import { Button } from '@/components/base/Button';

/**
 * Content structure for the NoResultsState component
 */
export interface NoResultsStateContent {
  /**
   * Main title text displayed prominently
   */
  title: string;
  /**
   * Descriptive text explaining the empty state and potential actions
   */
  description: string;
  /**
   * Optional text for the action button
   */
  actionButtonText?: string;
  /**
   * Optional icon name from lucide-react (e.g., 'AlertTriangle', 'SearchX')
   */
  icon?: string;
}

export interface NoResultsStateProps {
  /**
   * Content object containing all text and configuration
   */
  content: NoResultsStateContent;
  /**
   * Visual variant of the component
   * - 'modal': Displayed as overlay with close button styling
   * - 'inline': Displayed within the page content area
   * @default 'inline'
   */
  variant?: 'modal' | 'inline';
  /**
   * Callback function when action button is clicked
   */
  onAction?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const variantStyles = {
  modal: 'bg-white rounded-lg shadow-lg p-6 max-w-md',
  inline: 'py-8 px-4',
} as const;

const iconContainerStyles = {
  modal: 'mb-4',
  inline: 'mb-6',
} as const;

/**
 * NoResultsState - Empty state component displayed when searches yield no results
 *
 * Used to communicate to users that their search or filter criteria returned no matching
 * results, with optional guidance on how to proceed.
 *
 * @example
 * ```tsx
 * // Inline variant (in content area)
 * <NoResultsState
 *   content={{
 *     title: "No results were found",
 *     description: "Try modifying your filters or expanding your search area.",
 *     actionButtonText: "Clear filters",
 *     icon: "AlertTriangle"
 *   }}
 *   onAction={() => clearFilters()}
 * />
 *
 * // Modal variant
 * <NoResultsState
 *   content={{
 *     title: "No services found",
 *     description: "We couldn't find any services in this location.",
 *     icon: "SearchX"
 *   }}
 *   variant="modal"
 * />
 * ```
 */
export const NoResultsState = forwardRef<HTMLDivElement, NoResultsStateProps>(
  ({ content, variant = 'inline', onAction, className }, ref) => {
    if (!content) {
      return null;
    }

    const { title, description, actionButtonText, icon } = content;

    // Get the icon component if provided
    const IconComponent: LucideIcon | null = icon ? getIcon(icon) : null;

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          'flex flex-col items-center text-center',
          variantStyles[variant],
          className
        )}
      >
        {/* Icon */}
        {IconComponent && (
          <div className={cn('flex-shrink-0', iconContainerStyles[variant])}>
            <IconComponent
              className="h-10 w-10 text-amber-500"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Title */}
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {title}
        </h2>

        {/* Description */}
        <p className="text-muted-foreground max-w-sm mb-4">
          {description}
        </p>

        {/* Action Button */}
        {actionButtonText && onAction && (
          <Button
            variant="oh-secondary"
            size="oh-default"
            onClick={onAction}
            className="mt-2"
          >
            {actionButtonText}
          </Button>
        )}
      </div>
    );
  }
);

NoResultsState.displayName = 'NoResultsState';
